/**
 * Type-safe utility functions for USASpending hooks
 */

/**
 * Asserts that the access token is present and returns it.
 * This is used in queryFn where we know the query is only enabled when token exists,
 * but TypeScript can't infer that the token will be non-null at runtime.
 *
 * @throws Error if accessToken is null or undefined
 */
export function assertToken(accessToken: string | null | undefined): string {
  if (!accessToken) {
    throw new Error(
      'Access token is required but was not available. This should not happen - the query should be disabled.',
    )
  }
  return accessToken
}

/**
 * Valid award categories from the USASpending API
 */
export type AwardCategory = 'contract' | 'grant' | 'other'

/**
 * Type guard to validate award category strings.
 * Returns 'other' for invalid values rather than throwing.
 */
export function toAwardCategory(value: string): AwardCategory {
  if (value === 'contract' || value === 'grant' || value === 'other') {
    return value
  }
  return 'other'
}

/**
 * Asserts a value is a number, useful for pageParam in infinite queries.
 * TanStack Query's pageParam comes as unknown and needs runtime validation.
 *
 * @throws Error if value is not a number
 */
export function assertPageNumber(value: unknown): number {
  if (typeof value === 'number') {
    return value
  }
  throw new Error(`Expected page number but got ${typeof value}`)
}
